import { useState, useRef, useEffect } from 'react'
import { LogOut, Plus, Building2, X, ShieldBan } from 'lucide-react'
import { useTheme, getThemeClass } from './ThemeProvider'
import { avatarBg, getInitials } from './avatar'
import { useTranslation } from '@papan/i18n'

interface TenantEmailInfo {
  email: string
  role: string
}

interface TenantInfo {
  id: string
  name: string
  type: string
  emails: TenantEmailInfo[]
}

interface PendingInvite {
  inviteId: string
  token: string
  tenantId: string
  tenantName: string
  email: string
  role: string
}

interface AvatarMenuProps {
  user: { name: string; email: string; image?: string }
  tenants: TenantInfo[]
  activeTenantId: string
  activeOrgEmail?: string
  pendingInvites?: PendingInvite[]
  onSwitchTenant: (tenantId: string, orgEmail?: string) => void
  onCreateOrg: () => void
  onAcceptInvite?: (token: string) => void
  onDeclineInvite?: (token: string) => void
  onBlockInvite?: (token: string) => void
  onSignOut: () => void
  /**
   * The viewer's OWN PERSONAL tenant is identity-only (0175, §8.1).
   *
   * Drops the Personal entry from the switcher: an identity-only tenant has no
   * drive, no workspace and nothing to land on, so offering it as a destination
   * is offering a dead end. The orgs, the invites and Log out all stay.
   *
   * ⚠ Deliberately NOT "the active tenant is identity-only", which is what this
   * prop used to receive and is a different question. Once such a person
   * switches into one of their organizations the active tenant is full, so that
   * flag goes false and the menu offered "Personal" again — one click back to a
   * drive that does not exist. The entry must stay hidden for as long as the
   * tenant BEHIND it is dead, not merely while the person is standing on it.
   *
   * Threaded from the shell's ONE derivation rather than resolved here — there
   * are already three spellings of the mode and a fourth is how they drift
   * (§9.2). Absent = show the entry, matching `isIdentityOnlyTenant`'s
   * fail-open default; this is disclosure, not enforcement.
   */
  personalIsIdentityOnly?: boolean
  /**
   * May this user create an organization (§8.7)?
   *
   * `undefined` is UNRESOLVED and renders the button — see `useOrgCreateLimit`.
   * Only a resolved `false` hides it, so a slow `create-limit` response can
   * never look like a refusal.
   */
  canCreateOrg?: boolean
  /** Where the menu opens. 'rail' (default) anchors above the desktop
   *  taskbar rail's bottom avatar; 'top-right' drops below a top bar's
   *  right-side avatar (the mobile header). Pure positioning.
   *
   *  ('bottom-center' lived here for the identity shell, which now renders its
   *  own footer and does not mount this component at all.) */
  placement?: 'rail' | 'top-right'
}

export function AvatarMenu({ user, tenants, activeTenantId, activeOrgEmail, pendingInvites, onSwitchTenant, onCreateOrg, onAcceptInvite, onDeclineInvite, onBlockInvite, onSignOut, personalIsIdentityOnly, canCreateOrg, placement = 'rail' }: AvatarMenuProps) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const [confirmBlockToken, setConfirmBlockToken] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  // §8.1: an identity-only tenant is not a destination, so it is not offered as
  // one. `undefined` here is what removes the entry — the rest of the menu is
  // untouched.
  const personal = personalIsIdentityOnly ? undefined : tenants.find(t => t.type === 'personal')
  const orgs = tenants.filter(t => t.type === 'organization')
  // Only a RESOLVED refusal hides it (§9.1) — `undefined` still renders.
  const showCreateOrg = canCreateOrg !== false
  /**
   * "Switch to" is a heading over DESTINATIONS, so it renders only when there
   * are some. "New organization" is not one — it is an action that happens to
   * live under that heading when destinations exist ("…or make a new one").
   *
   * Counting the create button as a destination produced a menu reading
   * "SWITCH TO → New organization" for a person with no organizations: a label
   * over the one thing it does not describe. That is the org-less screen, where
   * the menu is the entire surface, so it was the worst place to be incoherent.
   *
   * (The identity shell's PICKER does not use this component at all — its cards
   * are the switcher and its two actions sit beside the avatar, so there was
   * nothing left for a dropdown to hold.)
   */
  const hasDestinations = Boolean(personal) || orgs.length > 0
  const showSwitcher = hasDestinations
  /** Alongside the destinations when there are any… */
  const showCreateInSwitcher = showSwitcher && showCreateOrg
  /** …otherwise with the other account actions, where it belongs on its own. */
  const showCreateInActions = !hasDestinations && showCreateOrg
  const activeTenant = tenants.find(t => t.id === activeTenantId)
  const isOrg = activeTenant?.type === 'organization'
  const avatarLabel = isOrg ? getInitials(activeTenant.name) : (user.image ? '' : getInitials(user.name))
  const avatarTitle = isOrg ? activeTenant.name : user.name
  // Name-derived color (shared avatarBg) so the avatar matches this identity
  // everywhere: personal seeds on the email — same as the WhoHasAccess audit
  // avatars — an org on its name.
  const avatarColor = avatarBg(isOrg ? activeTenant.name : user.email)

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className={`relative w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium text-white transition-[filter] shrink-0 mx-auto mb-2 ${avatarColor} hover:brightness-110`}
        aria-label={avatarTitle}
        // Where the identity portal (IdentitySwitchCurtain) opens from and lands back on.
        data-identity-avatar=""
        data-tooltip={avatarTitle}
        // Beside the avatar only on the vertical rail; anywhere laid out
        // horizontally has a neighbour there. Same rule as NotificationBell.
        data-tooltip-placement={placement === 'rail' ? 'right' : undefined}
      >
        {!isOrg && user.image ? (
          <img src={user.image} alt="" className="w-full h-full rounded-full object-cover" />
        ) : (
          avatarLabel
        )}
        {(() => {
          const badge = pendingInvites?.length ?? 0
          return badge > 0 ? (
            <span className="absolute -top-0.5 right-[-3px] w-4.5 h-4.5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white">
              {badge}
            </span>
          ) : null
        })()}
      </button>

      {open && (
        <div
          ref={menuRef}
          // The glass menu (docs/DESIGN_DOC.md §10.5 — one menu look): rows are `ws-menu-item`s, sections split by
          // `ws-menu-sep`, and the identity you act as is a FILLED row (rule 7: state is fill, never a drawn mark).
          className={`${themeClass} ws-menu ws-anim-menu-in fixed z-50 w-72`}
          style={
            placement === 'top-right' ? { top: 56, right: 8, transformOrigin: 'top right' }
            : { bottom: 12, left: 60, transformOrigin: 'bottom left' }
          }
        >
          {/* Who you are */}
          <div className="flex items-center gap-2.5 px-2.5 pt-2 pb-1.5">
            <IdentityDot seed={user.email} label={user.name} image={user.image} />
            <div className="min-w-0">
              <div className="text-sm font-medium themed-text truncate">{user.name}</div>
              <div className="text-xs themed-text-muted truncate">{user.email}</div>
            </div>
          </div>

          {/* Tenant switcher — one row per identity: the personal account, and each org once per email you hold
              there (no nested expand), each with its own avatar and your role there. */}
          {showSwitcher && (
            <>
              <div className="ws-menu-sep" />
              <div className="px-2.5 pt-1 pb-1 text-[11px] uppercase tracking-wide themed-text-muted">Switch to</div>
              {personal && (
                <IdentityRow
                  index={0}
                  seed={user.email}
                  name={user.name}
                  image={user.image}
                  detail="Personal"
                  active={activeTenantId === personal.id}
                  onClick={() => { onSwitchTenant(personal.id); setOpen(false) }}
                />
              )}
              {orgs.flatMap(org => org.emails.map(e => ({ org, e }))).map(({ org, e }, i) => (
                <IdentityRow
                  key={`${org.id}:${e.email}`}
                  index={i + (personal ? 1 : 0)}
                  seed={org.name}
                  name={org.name}
                  detail={e.email}
                  role={e.role}
                  active={activeTenantId === org.id && (org.emails.length === 1 || activeOrgEmail === e.email)}
                  onClick={() => { onSwitchTenant(org.id, e.email); setOpen(false) }}
                />
              ))}
              {showCreateInSwitcher && (
                <button type="button" onClick={() => { onCreateOrg(); setOpen(false) }} className="ws-menu-item text-blue-400">
                  <span className="w-7 shrink-0 flex justify-center"><Plus size={15} /></span>
                  <span>New organization</span>
                </button>
              )}
            </>
          )}

          {/* Invitations */}
          {pendingInvites && pendingInvites.length > 0 && (
            <div className="py-1"><div className="ws-menu-sep" />
              <div className="px-3 py-1 text-xs themed-text-muted uppercase tracking-wide flex items-center gap-1.5">
                Invitations
                <span className="bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{pendingInvites.length}</span>
              </div>
              {pendingInvites.map(inv => (
                <div key={inv.inviteId} className="px-3 py-2 hover:themed-bg-tertiary transition-colors">
                  <div className="flex items-center gap-1.5">
                    <Building2 size={12} className="shrink-0 themed-text-muted" />
                    <span className="text-sm themed-text truncate">{inv.tenantName}</span>
                  </div>
                  <div className="text-[10px] themed-text-muted mt-0.5 truncate">
                    {inv.email} · {t(`roles.${inv.role}`, { defaultValue: inv.role })}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      onClick={() => { onAcceptInvite?.(inv.token); setOpen(false) }}
                      className="px-2.5 py-1 text-[11px] font-semibold bg-green-600 hover:bg-green-500 text-white rounded transition-colors"
                    >
                      Accept
                    </button>
                    <span className="relative group/decline">
                      <button
                        onClick={() => { onDeclineInvite?.(inv.token) }}
                        className="p-1 themed-text-muted hover:themed-text hover:themed-bg-tertiary rounded transition-colors"
                      >
                        <X size={16} strokeWidth={2.5} />
                      </button>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-[10px] themed-bg-tertiary themed-text themed-border border rounded shadow-lg whitespace-nowrap opacity-0 pointer-events-none group-hover/decline:opacity-100 transition-opacity">
                        Decline
                      </span>
                    </span>
                    {confirmBlockToken === inv.token ? (
                      <button
                        onClick={() => { onBlockInvite?.(inv.token); setConfirmBlockToken(null) }}
                        className="px-2 py-1 text-[10px] font-semibold text-red-400 hover:bg-red-500/20 rounded transition-colors"
                      >
                        Confirm block
                      </button>
                    ) : (
                      <span className="relative group/block">
                        <button
                          onClick={() => setConfirmBlockToken(inv.token)}
                          className="p-1 themed-text-muted hover:text-red-400 hover:themed-bg-tertiary rounded transition-colors"
                        >
                          <ShieldBan size={16} strokeWidth={2.5} />
                        </button>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-[10px] themed-bg-tertiary themed-text themed-border border rounded shadow-lg whitespace-nowrap opacity-0 pointer-events-none group-hover/block:opacity-100 transition-opacity">
                          Block
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="ws-menu-sep" />
          {/* With no destinations there is no "Switch to" section to carry this, and it is the one thing an
              org-less person can still do besides accepting an invitation. */}
          {showCreateInActions && (
            <button type="button" onClick={() => { onCreateOrg(); setOpen(false) }} className="ws-menu-item text-blue-400">
              <span className="w-7 shrink-0 flex justify-center"><Plus size={15} /></span>
              <span>New organization</span>
            </button>
          )}
          <button type="button" onClick={() => { onSignOut(); setOpen(false) }} className="ws-menu-item ws-menu-item-danger">
            <span className="w-7 shrink-0 flex justify-center"><LogOut size={15} /></span>
            <span>Log out</span>
          </button>
        </div>
      )}
    </>
  )
}

/** The person's role in an org, beside it in the switcher (owner, Drive Admin, member…). */
function RoleTag({ role }: { role?: string }) {
  const { t } = useTranslation('common')
  if (!role) return null
  return <span className="shrink-0 text-xs themed-text-secondary">{t(`roles.${role}`, { defaultValue: role })}</span>
}

/** An identity's avatar, small — the same name-derived gradient as the taskbar avatar (a photo when the person has
 *  one), so each row is recognisable at a glance. */
function IdentityDot({ seed, label, image }: { seed: string; label: string; image?: string }) {
  if (image) return <img src={image} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
  return (
    <span aria-hidden className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[11px] font-semibold text-white ${avatarBg(seed)}`}>
      {getInitials(label)}
    </span>
  )
}

/** One identity in the switcher: avatar, name over the email (or "Personal"), the role right-aligned. */
function IdentityRow({ index, seed, name, image, detail, role, active, onClick }: {
  index: number; seed: string; name: string; image?: string; detail: string; role?: string; active: boolean; onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      style={{ ['--i' as string]: index }}
      className={`ws-menu-item ws-anim-row ${active ? 'bg-[var(--ws-pill-active-bg)]' : ''}`}
    >
      <IdentityDot seed={seed} label={name} image={image} />
      <span className="min-w-0 flex-1">
        <span className="block truncate">{name}</span>
        {/* Secondary, not muted: inside a menu row the kit pins muted text to a fixed light grey (#b9b9b9), which
            nearly vanishes on the light glass. */}
        <span className="block truncate text-xs themed-text-secondary">{detail}</span>
      </span>
      <RoleTag role={role} />
    </button>
  )
}
